// Redesigned to match the CodeBruh site's content-page template (see MissionPage / CopyrightPage / TermsPage).
import {
  ArrowLeftIcon,
  ClockIcon,
  ExternalLinkIcon,
  GlobeIcon,
  Helmet,
  InstagramIcon,
  LinkedinIcon,
  MailIcon,
  MapPinIcon,
  motion,
} from "../vendor/runtime.js";

const ContactPage = () => {
  const goBack = () => {
      window.history.back();
    },
    fadeUp = {
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    },
    stagger = {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
    },
    cards = [
      {
        id: 1,
        icon: MailIcon,
        title: "Email Support",
        color: "from-blue-500 to-cyan-500",
        content:
          "The fastest way to reach us. Whether it's a question about an order, a bug report, or a custom project request, our team typically replies within 24-48 hours on business days.",
        email: "codebruh@kestroinfotech.com",
      },
      {
        id: 2,
        icon: MapPinIcon,
        title: "Business Address",
        color: "from-purple-500 to-indigo-500",
        content: (
          <address className={"not-italic"}>
            {"45, New Colony,"}
            <br />
            {"Villupuram,"}
            <br />
            {"Tamil Nadu - 605602,"}
            <br />
            {"India"}
          </address>
        ),
      },
      {
        id: 3,
        icon: ClockIcon,
        title: "Response Time",
        color: "from-green-500 to-emerald-500",
        content:
          "We aim to respond to every message within 24-48 hours, Monday through Saturday. Urgent order or payment issues are prioritized.",
      },
      {
        id: 4,
        icon: GlobeIcon,
        title: "Connect With Us",
        color: "from-orange-500 to-red-500",
        content: "Follow CodeBruh for updates, new project drops, and student tips.",
        socials: [
          {
            name: "LinkedIn",
            Icon: LinkedinIcon,
            url: "https://www.linkedin.com/company/codebruhofficial",
          },
          {
            name: "Instagram",
            Icon: InstagramIcon,
            url: "https://www.instagram.com/codebruh.in/",
          },
        ],
      },
    ];

  return (
    <div className={"min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100"}>
      <Helmet>
        <title>{"Contact Us | CodeBruh"}</title>
        <meta
          name={"description"}
          content={
            "Get in touch with CodeBruh — email support, our business address, and social links."
          }
        />
      </Helmet>

      <section
        className={
          "bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-900 relative overflow-hidden py-12 lg:py-16"
        }
      >
        <div className={"absolute inset-0 opacity-10"}>
          <div
            className={
              "absolute top-0 left-0 w-full h-full bg-gradient-to-br from-cyan-400/20 via-blue-500/20 to-purple-600/20"
            }
          />
          <div
            className={
              "absolute top-1/4 left-1/4 w-20 h-20 lg:w-32 lg:h-32 bg-white/5 rounded-full blur-2xl animate-pulse"
            }
          />
          <div
            className={
              "absolute bottom-1/4 right-1/4 w-16 h-16 lg:w-24 lg:h-24 bg-indigo-400/10 rounded-full blur-xl animate-pulse delay-1000"
            }
          />
        </div>
        <div className={"relative mx-auto px-3 sm:px-4"}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className={"mb-4 sm:mb-6 lg:mb-8"}
          >
            <button
              onClick={goBack}
              className={
                "group inline-flex items-center px-3 sm:px-4 lg:px-6 py-2 lg:py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg lg:rounded-xl hover:bg-white/20 transition-all duration-300 text-xs sm:text-sm lg:text-base font-medium shadow-lg hover:shadow-xl"
              }
            >
              <ArrowLeftIcon
                className={
                  "w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 mr-1.5 sm:mr-2 group-hover:-translate-x-1 transition-transform duration-300"
                }
              />
              {"Go Back"}
            </button>
          </motion.div>
          <div className={"text-center"}>
            <motion.div initial={"hidden"} animate={"visible"} variants={fadeUp} className={"text-white"}>
              <div
                className={
                  "w-12 h-12 sm:w-14 sm:h-14 lg:w-20 lg:h-20 mx-auto mb-4 lg:mb-6 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl lg:rounded-2xl flex items-center justify-center shadow-2xl"
                }
              >
                <MailIcon className={"w-6 h-6 sm:w-7 sm:h-7 lg:w-10 lg:h-10 text-white"} />
              </div>
              <h1
                className={
                  "text-xl sm:text-2xl lg:text-4xl font-bold mb-3 lg:mb-4 bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent"
                }
              >
                {"Get In Touch"}
              </h1>
              <p className={"text-sm sm:text-base lg:text-xl text-slate-300 mx-auto leading-relaxed mb-4"}>
                {"Questions, feedback, or a custom project in mind? We'd love to hear from you."}
              </p>
              <div
                className={
                  "inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20"
                }
              >
                <div className={"w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"} />
                <span className={"text-xs lg:text-sm text-slate-300 font-medium"}>
                  {"Usually replies within 24-48 hours"}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <main className={"mx-auto px-3 sm:px-4 py-6 sm:py-8 lg:py-12 -mt-6 sm:-mt-8 lg:-mt-12 relative z-10"}>
        <motion.div
          initial={"hidden"}
          animate={"visible"}
          variants={stagger}
          className={"space-y-6 sm:space-y-8 lg:space-y-12"}
        >
          <div className={"grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8"}>
            {cards.map((c) => (
              <motion.div
                key={c.id}
                variants={fadeUp}
                className={
                  "bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-xl border border-slate-100 overflow-hidden group hover:shadow-2xl transition-all duration-300 flex flex-col"
                }
              >
                <div className={"p-4 sm:p-6 lg:p-8 flex-1"}>
                  <div className={"flex items-start gap-3 sm:gap-4"}>
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gradient-to-br ${c.color} rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <c.icon className={"w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white"} />
                    </div>
                    <div className={"flex-1 min-w-0"}>
                      <h3 className={"text-base sm:text-lg lg:text-xl font-bold text-slate-900 mb-2 sm:mb-3"}>
                        {c.title}
                      </h3>
                      <div className={"text-xs sm:text-sm lg:text-base text-slate-600 leading-relaxed mb-4"}>
                        {c.content}
                      </div>
                      {c.email && (
                        <a
                          href={`mailto:${c.email}`}
                          className={
                            "inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-xs sm:text-sm font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                          }
                        >
                          <MailIcon className={"w-4 h-4 mr-2"} />
                          {c.email}
                        </a>
                      )}
                      {c.socials && (
                        <div className={"flex flex-wrap gap-3"}>
                          {c.socials.map(({ name, Icon, url }) => (
                            <a
                              key={name}
                              href={url}
                              target={"_blank"}
                              rel={"noopener noreferrer"}
                              className={
                                "inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-xs sm:text-sm font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              }
                            >
                              <Icon className={"w-4 h-4 mr-2"} />
                              {name}
                              <ExternalLinkIcon className={"w-3 h-3 ml-2 opacity-70"} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`h-0.5 sm:h-1 bg-gradient-to-r ${c.color}`} />
              </motion.div>
            ))}
          </div>

          <motion.div
            variants={fadeUp}
            className={
              "bg-gradient-to-r from-slate-900 to-indigo-900 rounded-lg sm:rounded-xl lg:rounded-2xl p-6 sm:p-8 lg:p-12 text-center relative overflow-hidden"
            }
          >
            <div className={"absolute inset-0 opacity-10"}>
              <div
                className={
                  "absolute top-1/4 left-1/4 w-16 h-16 lg:w-24 lg:h-24 bg-white/20 rounded-full blur-xl animate-pulse"
                }
              />
              <div
                className={
                  "absolute bottom-1/4 right-1/4 w-12 h-12 lg:w-20 lg:h-20 bg-cyan-400/20 rounded-full blur-lg animate-pulse delay-500"
                }
              />
            </div>
            <div className={"relative"}>
              <h3 className={"text-base sm:text-lg lg:text-xl font-bold text-white mb-3 sm:mb-4"}>
                {"Still Have Questions?"}
              </h3>
              <p className={"text-xs sm:text-sm lg:text-base text-slate-300 mb-4 sm:mb-6 mx-auto"}>
                {"Drop us a line and our team will get back to you as soon as possible."}
              </p>
              <a
                href={"mailto:codebruh@kestroinfotech.com"}
                className={
                  "inline-flex items-center px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-lg lg:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-xs sm:text-sm lg:text-base"
                }
              >
                <MailIcon className={"w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 mr-1.5 sm:mr-2"} />
                {"Email codebruh@kestroinfotech.com"}
              </a>
            </div>
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
};

export { ContactPage };
